import { FunctionComponent } from 'react'
import { useAtomValue } from 'jotai'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { currentScopeState } from '~/state'
import { UPGRADE_OFFER_CTA, UPGRADE_OFFER_TITLE, upgradeRequestTrackingLabel } from '~/modules/monetization/upgrade/upgradeOffer'
import { UpgradeChip } from '~/modules/monetization/upgrade/UpgradeChip'
import { contactUsUrl } from '~/utils/hubspot'
import { BidderRuleFormSection } from './bidderRuleSections'
import { BLOCKED_SECTION_OFFER } from './blockedReason'

type Props = {
  section: BidderRuleFormSection | 'spark'
  /** Whether the rule already holds seats here, which are saved untouched either way. */
  hasSeats: boolean
}

/**
 * What a section the account cannot edit shows instead of its list.
 *
 * It used to be a flat grey sentence naming what was missing. That is accurate and useless: it tells a
 * publisher they cannot do the thing and stops, leaving the one action that would change it — asking —
 * off the screen. A single row, the width of the list it replaces: the offer, one line on what it opens,
 * and the ask.
 *
 * Kept to one row on purpose. The section is the part of the page the reader did *not* come for, and a
 * full pitch here would outweigh the rule they are actually editing.
 */
export const BlockedSectionUpsell: FunctionComponent<Props> = ({ section, hasSeats }) => {
  const isSpark = section === 'spark'
  const offer = isSpark ? 'node' : BLOCKED_SECTION_OFFER[section]
  const user = useAuthenticatedUser()
  const scope = useAtomValue(currentScopeState)

  return (
    <div className="border-platinum bg-ghost flex min-h-25 items-center gap-4 rounded-lg border px-4 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <UpgradeChip className="text-sm font-medium">{isSpark ? SPARK_TITLE : UPGRADE_OFFER_TITLE[offer]}</UpgradeChip>
        <p className="text-hurricane text-sm">
          {isSpark ? SPARK_LINE : UPGRADE_OFFER_SECTION_LINE[offer]}
          {hasSeats && ' The seats this rule already holds here stay in it and are saved unchanged.'}
        </p>
      </div>
      <Button
        className="ml-auto shrink-0"
        variant="secondary"
        onClick={() => window.open(contactUsUrl({ email: user.profile.email, scopeName: scope.name }), '_blank', 'noopener,noreferrer')}
        trackingLabel={upgradeRequestTrackingLabel(offer)}
      >
        {isSpark ? SPARK_CTA : UPGRADE_OFFER_CTA[offer]}
      </Button>
    </div>
  )
}

/** One line, because the card is one row. The full pitch lives on the upgrade surfaces. */
const UPGRADE_OFFER_SECTION_LINE = {
  one: 'Client-side bidders are deployed with One.',
  node: 'Server-side bidding runs on Node, or on Spark for Publishers.',
} as const

const SPARK_TITLE = 'Get server-side demand with Spark'
const SPARK_LINE = 'Spark Demand and Spark for Publishers add server-side demand to your stack.'
const SPARK_CTA = 'Talk to us about Spark'
